// @flow
import React, { useContext, useCallback, type Node } from 'react'
import cx from 'classnames'
import { useSelector } from 'react-redux'
import {
    Breadcrumbs,
    Box,
    Button,
    DisplayHtmlContent,
    Footer,
    Heading,
    Icons,
    Notification,
    Stack,
} from 'sarsaparilla'

import { permitSelector } from 'ui-permit/shared-components/slices/permitContent';

import { LayoutControlContext } from './DetailedAvailabilityPageLayout'
import ExpandMainViewButton from './ExpandMainViewButton'
import DetailedAvailabilityFilter from './DetailedAvailabilityFilter'
import DetailedAvailabilityGridAndActions from './DetailedAvailabilityGridAndActions'
import DetailedAvailabilityInformationRequired from './DetailedAvailabilityInformationRequired'
import ErrorNotification from '../common/NewErrorNotification'

import { type Breadcrumb } from '../types'

type PropTypes = {
    breadcrumbPath?: Breadcrumb[],
    errorIsShow: boolean,
    errorDismiss?: Function,
    errorBody?: string,
    warningIsShow?: boolean,
    warningDismiss?: Function,
    warningBody?: string,
    children: Node,
}
const DetailedAvailabilityPageMain = ({
    breadcrumbPath = [],
    errorBody,
    errorDismiss,
    errorIsShow,
    warningBody,
    warningDismiss,
    warningIsShow,
    children,
}: PropTypes) => {
    const permit = useSelector(permitSelector.getData)
    const {
        isFullView,
        isInformationRequired,
        isMobileApp,
        isShowAvailabilityFilters,
        setIsFullView,
        setIsShowAvailabilityFilters,
    } = useContext(LayoutControlContext);

    const handleToggleFullView = useCallback(() => {
        setIsFullView((prevIsFullView) => !prevIsFullView)
    }, [setIsFullView])

    const childrenArray = React.Children.toArray(children);

    const detailedAvailabilityGridAndActions = childrenArray.find(({ type }) => type === DetailedAvailabilityGridAndActions);
    const detailedAvailabilityInformationRequired = childrenArray.find(({ type }) => type === DetailedAvailabilityInformationRequired);
    const detailedAvailabilityExtraComponents = childrenArray.filter(({ props }) => props.isAllowOnDetailedAvailability);

    let detailedAvailabilityFilterWithProps = childrenArray.find(({ type }) => type === DetailedAvailabilityFilter)
    if (detailedAvailabilityFilterWithProps && React.isValidElement(detailedAvailabilityFilterWithProps))
    {
        detailedAvailabilityFilterWithProps = React.cloneElement(
            detailedAvailabilityFilterWithProps,
            undefined,
            [
                ...React.Children.toArray(detailedAvailabilityFilterWithProps.props.children),
                detailedAvailabilityInformationRequired
            ])
    }

    const renderNotifications = () => {
        return (errorIsShow || warningIsShow) && (
            <div
                className={cx('per-availability-notification', {
                    'mobile-app': isMobileApp
                })}>
                <ErrorNotification
                    className="mb-2"
                    body={errorBody}
                    dismiss={errorDismiss}
                    isShow={errorIsShow}
                />
                {warningIsShow && (
                    <Notification
                        className="mb-2"
                        type="warning"
                        dismissNotification={warningDismiss} >
                        {warningBody}
                    </Notification>
                )}
            </div>
        );
    }

    return (
        <div
            id="per-availability-main"
            className={cx('per-availability-main', {
                'expand': isFullView,
                'mobile-app': !isShowAvailabilityFilters && isMobileApp
            })}>
            <Stack role="main" space="md">
                {isMobileApp && !isShowAvailabilityFilters && renderNotifications()}

                {!!permit?.description?.availability_grid_alt && !isShowAvailabilityFilters && (
                    <Box className="rec-sr-only">
                        <DisplayHtmlContent html={permit?.description?.availability_grid_alt} />
                    </Box>
                )}

                {detailedAvailabilityFilterWithProps}

                {(!isMobileApp || (isShowAvailabilityFilters !== isMobileApp)) && (
                    <div className="title-navigation">
                        {!!breadcrumbPath?.length && <Breadcrumbs path={breadcrumbPath} />}
                        <ExpandMainViewButton
                            isExpanded={isFullView}
                            onClick={handleToggleFullView} />
                    </div>
                )}

                {!isMobileApp && renderNotifications()}

                {(!isShowAvailabilityFilters || !isMobileApp) && (
                    <Heading headingLevel={1} appearance="h4">
                        {isMobileApp && (
                            <Button
                                appearance="pill"
                                size="md"
                                onClick={() => setIsShowAvailabilityFilters(true)}
                                iconBeforeElement={<Icons.IconArrowBack />}>
                                Change filters
                            </Button>
                        )}
                        {!isMobileApp && "Detailed Availability"}
                    </Heading>
                )}
                {detailedAvailabilityExtraComponents}

                {!!permit?.description?.availability_grid && !isShowAvailabilityFilters && (
                    <DisplayHtmlContent html={permit?.description?.availability_grid} />
                )}

                {(isInformationRequired && !isMobileApp) && ( detailedAvailabilityInformationRequired )}

                {detailedAvailabilityGridAndActions}
            </Stack>

            <div className="results-footer-wrap rec-sticky-footer">
                <Footer inline />
            </div>
        </div>
    )
}

export default DetailedAvailabilityPageMain;
